/**
 * Training practice-day checklist page.
 *
 * Renders a hand-authored checklist with a per-item toggle. The
 * backend tracks completion as a single row — the frontend uses
 * local state for the per-item ticks, then posts the overall
 * completion when all items are checked AND the learner confirms
 * via the "Pass this practice day" button.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { extractApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, CheckCircle2, Circle, ArrowRight } from "lucide-react";


export default function TrainingPracticeDay() {
  const { dayId } = useParams();
  const nav = useNavigate();
  const [state, setState] = useState({ day: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [upgradeNeeded, setUpgradeNeeded] = useState(false);
  const [checked, setChecked] = useState({});
  const [scores, setScores] = useState({});
  const [confirming, setConfirming] = useState(false);
  const [passed, setPassed] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/training/practice-days/${dayId}`);
        setState({ day: r.data?.practice_day || null });
        if (r.data?.practice_day?.completed) setPassed(true);
      } catch (err) {
        if (err?.response?.status === 402) {
          setUpgradeNeeded(true);
          setError(extractApiError(
            err,
            "This practice day is included with the AI Training packages and memberships.",
          ));
        } else {
          setError(extractApiError(err, "Couldn't load this practice day."));
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [dayId]);

  function toggleItem(i) {
    setChecked((c) => ({ ...c, [i]: !c[i] }));
  }

  function setCriterionScore(cid, score) {
    setScores((s) => ({ ...s, [cid]: score }));
  }

  async function onConfirmPass() {
    setConfirming(true);
    try {
      // R36 (Defect-2 fix): include self-scores on skill-check
      // completions so the server can enforce the completion rule
      // (self_score_min_6_no_zero). The server rejects with 400 when
      // the rule is not met; non-skill-check rows ignore this field.
      const body = {
        lesson_id: dayId,
        kind:      "practice_day",
      };
      if (day?.is_skill_check && completionRule === "self_score_min_6_no_zero") {
        body.self_scores = criteria.reduce(
          (acc, c) => ({ ...acc, [c.id]: scores[c.id] ?? 0 }),
          {},
        );
      }
      await api.post("/training/progress", body);
      setPassed(true);
    } catch (err) {
      setError(extractApiError(err, "Couldn't record practice-day completion."));
    } finally {
      setConfirming(false);
    }
  }

  const day = state.day;
  const items = day?.checklist || [];
  const criteria = day?.self_score_criteria || [];
  const completionRule = day?.completion_rule || "checklist";

  // Round-16: Month-4 knowledge-check practice-day rows redirect
  // to the dedicated knowledge-check page so the learner doesn't
  // land on an empty checklist shell.
  const isKnowledgeCheck = !!day?.is_knowledge_check;
  const kcSection = day?.knowledge_check_section;
  useEffect(() => {
    if (!loading && isKnowledgeCheck && kcSection) {
      nav(`/training/month-knowledge-check/${kcSection}`, { replace: true });
    }
  }, [loading, isKnowledgeCheck, kcSection, nav]);

  // Completion-rule computation.
  const allChecked = items.length > 0 && items.every((_, i) => checked[i]);
  const scoreTotal = criteria.reduce((acc, c) => acc + (scores[c.id] ?? 0), 0);
  const scoreMinOk =
    criteria.length > 0 &&
    criteria.every((c) => scores[c.id] !== undefined && scores[c.id] > 0);
  const selfScoreOk =
    completionRule === "self_score_min_6_no_zero" &&
    scoreMinOk && scoreTotal >= 6;
  const confirmReady =
    completionRule === "self_score_min_6_no_zero"
      ? selfScoreOk
      : allChecked;

  return (
    <AppShell>
      <div
        data-testid="training-practice-day-page"
        className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-8 sm:py-12"
      >
        <Button
          variant="ghost"
          size="sm"
          onClick={() => nav("/training")}
          data-testid="training-practice-day-back-btn"
          className="mb-4 text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"
        >
          <ArrowLeft className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
          Back to AI Training
        </Button>

        {loading && (
          <p
            data-testid="training-practice-day-loading"
            className="text-sm text-fg-secondary text-center py-12"
          >
            Loading practice day&hellip;
          </p>
        )}

        {!loading && upgradeNeeded && (
          <>
            <Alert
              variant="destructive"
              data-testid="training-practice-day-paywall"
              className="mb-4"
            >
              <AlertDescription>{error}</AlertDescription>
            </Alert>
            <Link to="/plans">
              <Button
                data-testid="training-practice-day-paywall-plans-btn"
                className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
              >
                See AI Training packages
              </Button>
            </Link>
          </>
        )}

        {!loading && !upgradeNeeded && error && (
          <Alert
            variant="destructive"
            data-testid="training-practice-day-error"
            className="mb-4"
          >
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {!loading && !error && day && (
          <article>
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <Badge
                data-testid="training-practice-day-label"
                className="bg-bg-surface-hover text-fg-secondary hover:bg-bg-surface-hover uppercase text-[10px] tracking-widest border border-border-subtle"
              >
                {day.label}
              </Badge>
              {day.is_skill_check && (
                <Badge
                  data-testid="training-practice-day-skill-check-badge"
                  className="bg-accent-warm text-bg-base hover:bg-accent-warm uppercase text-[10px] tracking-widest"
                >
                  Skill check
                </Badge>
              )}
              {passed && (
                <Badge
                  data-testid="training-practice-day-passed-badge"
                  className="bg-transparent text-fg-primary hover:bg-transparent uppercase text-[10px] tracking-widest border border-accent-warm/60"
                >
                  Marked complete
                </Badge>
              )}
            </div>
            <h1
              data-testid="training-practice-day-title"
              className="text-2xl sm:text-3xl font-heading font-medium text-fg-primary leading-tight"
            >
              {day.title}
            </h1>

            <section
              data-testid="training-practice-day-checklist"
              className="mt-6 rounded-lg border border-border-subtle bg-bg-surface p-5"
            >
              <h2 className="text-sm font-heading font-medium text-fg-primary">
                Checklist
              </h2>
              <ul className="mt-3 space-y-2">
                {items.map((item, i) => {
                  const isChecked = !!checked[i];
                  return (
                    <li key={i}>
                      <button
                        type="button"
                        data-testid={`training-practice-day-item-${i}`}
                        onClick={() => toggleItem(i)}
                        className="w-full text-left flex items-start gap-2 rounded-md px-2 py-1.5 text-sm text-fg-primary hover:bg-bg-surface-hover"
                      >
                        {isChecked ? (
                          <CheckCircle2 className="h-4 w-4 text-accent-warm flex-shrink-0 mt-0.5" aria-hidden="true" />
                        ) : (
                          <Circle className="h-4 w-4 text-fg-muted flex-shrink-0 mt-0.5" aria-hidden="true" />
                        )}
                        <span className="leading-relaxed">{item}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>

            {day.fresh_fixture && (
              <section
                data-testid="training-practice-day-fresh-fixture"
                className="mt-4 rounded-lg border border-progress-teal/40 bg-progress-teal/5 p-5"
              >
                <h2 className="text-sm font-heading font-medium text-fg-primary">
                  Fresh fixture
                </h2>
                <p className="mt-2 text-sm text-fg-primary leading-relaxed">
                  {day.fresh_fixture}
                </p>
              </section>
            )}

            {criteria.length > 0 && (
              <section
                data-testid="training-practice-day-self-score"
                className="mt-4 rounded-lg border border-accent-warm/40 bg-accent-warm/5 p-5"
              >
                <h2 className="text-sm font-heading font-medium text-fg-primary">
                  Self-score each criterion (0, 1, or 2)
                </h2>
                <p className="mt-1 text-xs text-fg-muted leading-relaxed">
                  0 = missing or incorrect &middot; 1 = partly demonstrated and needs revision &middot; 2 = demonstrated with evidence.
                </p>
                <div className="mt-3 space-y-3">
                  {criteria.map((c, idx) => {
                    const picked = scores[c.id];
                    return (
                      <div
                        key={c.id}
                        data-testid={`training-practice-day-criterion-${c.id}`}
                        className="rounded-md border border-border-subtle bg-bg-surface p-3"
                      >
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <p
                            data-testid={`training-practice-day-criterion-${c.id}-label`}
                            className="text-sm text-fg-primary leading-relaxed flex-1"
                          >
                            <strong className="mr-2">#{idx + 1}.</strong>
                            {c.label}
                          </p>
                          <div
                            role="radiogroup"
                            aria-label={`Score for ${c.label}`}
                            className="flex gap-1 flex-shrink-0"
                          >
                            {[0, 1, 2].map((n) => (
                              <button
                                key={n}
                                type="button"
                                role="radio"
                                aria-checked={picked === n}
                                data-testid={`training-practice-day-criterion-${c.id}-score-${n}`}
                                onClick={() => setCriterionScore(c.id, n)}
                                className={
                                  "w-10 h-10 text-base rounded-md border font-mono transition-colors " +
                                  (picked === n
                                    ? (n === 0
                                        ? "border-destructive bg-destructive/10 text-fg-primary"
                                        : n === 1
                                          ? "border-amber-500/60 bg-amber-500/10 text-fg-primary"
                                          : "border-progress-teal/60 bg-progress-teal/10 text-fg-primary")
                                    : "border-border-subtle bg-bg-base text-fg-primary hover:bg-bg-surface-hover")
                                }
                              >
                                {n}
                              </button>
                            ))}
                          </div>
                        </div>
                        {picked !== undefined && (
                          <p
                            data-testid={`training-practice-day-criterion-${c.id}-feedback`}
                            className={
                              "mt-2 text-xs leading-relaxed " +
                              (picked === 0
                                ? "text-destructive"
                                : "text-fg-secondary")
                            }
                          >
                            {picked === 0 ? c.zero_feedback :
                             picked === 1 ? c.one_feedback :
                                            c.two_feedback}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div
                  data-testid="training-practice-day-self-score-total"
                  className="mt-3 text-xs text-fg-primary"
                >
                  Total: <strong>{scoreTotal}</strong> / 8.{" "}
                  {completionRule === "self_score_min_6_no_zero" && (
                    <span className="text-fg-muted">
                      Completion requires ≥ 6 points and no criterion at zero.
                    </span>
                  )}
                </div>
              </section>
            )}

            {(day.recall || []).length > 0 && (
              <section
                data-testid="training-practice-day-recall"
                className="mt-4 rounded-lg border border-border-subtle bg-bg-surface p-5"
              >
                <h2 className="text-sm font-heading font-medium text-fg-primary">
                  Recall
                </h2>
                <ul className="mt-3 list-disc pl-5 space-y-1 text-sm text-fg-primary">
                  {day.recall.map((r, i) => (
                    <li key={i} data-testid={`training-practice-day-recall-${i}`}>
                      {r}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {day.pass_rule && (
              <Alert
                data-testid="training-practice-day-pass-rule"
                className="mt-4 border-amber-500/40 bg-amber-500/5 text-fg-primary"
              >
                <AlertDescription>
                  <strong>Pass rule.</strong> {day.pass_rule}
                </AlertDescription>
              </Alert>
            )}

            {day.completion_note && (
              <p
                data-testid="training-practice-day-completion-note"
                className="mt-4 text-sm text-fg-secondary leading-relaxed"
              >
                {day.completion_note}
              </p>
            )}

            <div className="mt-6 flex items-center gap-3">
              <Button
                data-testid="training-practice-day-confirm-btn"
                onClick={onConfirmPass}
                disabled={!confirmReady || confirming || passed}
                className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
              >
                {passed ? "Already marked complete" : confirming ? "Saving\u2026" : "Pass this practice day"}
              </Button>
              {!confirmReady && !passed && (
                <p
                  data-testid="training-practice-day-progress-note"
                  className="text-xs text-fg-muted"
                >
                  {completionRule === "self_score_min_6_no_zero"
                    ? "Score every criterion (no zero allowed) and reach at least 6 of 8 points."
                    : "Tick every item once you have done it."}
                </p>
              )}
            </div>
          </article>
        )}
      </div>
    </AppShell>
  );
}
